import { Loader2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/**
 * The two non-terminal states of a committed reveal, shared by both surfaces so they cannot
 * drift: the claim is in flight, or it failed.
 *
 * Both exist because of deferred disclosure (#3922): the prize is unknown until the claim
 * succeeds, so a reveal is a real round trip rather than an instant local transition. These
 * render inside whichever card/window chrome the surface already has.
 */

const PILL =
  "flex w-full items-center justify-center gap-2.5 rounded-full px-6 py-3.5 font-heading text-[15px] leading-none font-bold";

export const RevealClaiming = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex w-full flex-col gap-3", className)}>
      <div
        aria-live="polite"
        className={cn(PILL, "bg-gradient-cta text-background opacity-75")}
      >
        {/* Tailwind's animate-spin, not an inline `animation`: an inline style would override
            `motion-reduce:animate-none` and keep spinning for a player who asked it not to. */}
        <Loader2
          aria-hidden
          className="size-4.5 shrink-0 animate-spin motion-reduce:animate-none"
          strokeWidth={2.5}
        />
        {t("prizeReveal.revealing", "Revealing…")}
      </div>
      <p className="text-center font-body text-[12px] text-muted-foreground">
        {t("prizeReveal.keepOpen", "Keep this open for a moment.")}
      </p>
    </div>
  );
};

export const RevealFailed = ({
  count,
  onRetry,
  onDismiss,
  className,
}: {
  count: number;
  onRetry: () => void;
  onDismiss: () => void;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex w-full flex-col gap-4", className)}>
      <div className="flex items-center gap-2.5">
        <TriangleAlert
          aria-hidden
          className="size-5 shrink-0 text-urgent"
          strokeWidth={2}
        />
        <p className="font-heading text-[19px] leading-tight font-bold text-foreground">
          {t("prizeReveal.failedTitle", "Couldn't reveal your prizes")}
        </p>
      </div>
      {/* Says what happened and what to do, and the reassurance is literally true: the acks stay
          staged and retry on reconnect, and `finalize_finished_campaigns` credits anything still
          unclaimed at campaign close. */}
      <p
        aria-live="polite"
        className="font-body text-[13px] leading-relaxed text-foreground-secondary"
      >
        {t(
          "prizeReveal.failedBody",
          "Your {{count}} prizes are safe. Try again, or they'll be credited automatically when the campaign ends.",
          { count }
        )}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={cn(
          PILL,
          "bg-gradient-cta min-h-12 text-background transition-[filter] hover:brightness-105"
        )}
      >
        {t("prizeReveal.tryAgain", "Try again")}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="flex min-h-11 w-full items-center justify-center font-body text-[13px] font-medium text-foreground-secondary underline underline-offset-[3px]"
      >
        {t("prizeReveal.oneAtATime", "Reveal one at a time")}
      </button>
    </div>
  );
};
